import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import WebsiteInsightsToolbar from "./WebsiteInsightsToolbar";
import WebsiteConversionReport from "./WebsiteConversionReport";
import WebsiteAiInsights from "./WebsiteAiInsights";
import WebsiteTopCampaigns from "./WebsiteTopCampaigns";
import WebsiteContactSubscribePages from "./WebsiteContactSubscribePages";
import WebsiteContactLandingPages from "./WebsiteContactLandingPages";
import WebsiteContactReferralSources from "./WebsiteContactReferralSources";
import WebsiteContactUTMs from "./WebsiteContactUTMs";
import WebsiteCustomFields from "./WebsiteCustomFields";
import {
  buildInsightsCsvExportUrl,
  buildInsightsPdfExportUrl,
  syncInsightsTimeframeParamsToUrl,
  INSIGHTS_TIMEFRAME_CHANGE_EVENT,
} from "../../utils/insightsTimeframe";

const VALID_TABS = [
  "overview",
  "campaigns",
  "subscribe_pages",
  "landing_pages",
  "referral_sources",
  "utms",
  "custom_fields",
  "ai_insights",
];

const OVERVIEW_TOP_CAMPAIGNS_LIMIT = 8;

const CONTACT_TABS = [
  { key: "subscribe_pages", Component: WebsiteContactSubscribePages },
  { key: "landing_pages", Component: WebsiteContactLandingPages },
  { key: "referral_sources", Component: WebsiteContactReferralSources },
  { key: "utms", Component: WebsiteContactUTMs },
];

const normalizeInsight = (value) =>
  VALID_TABS.includes(value) ? value : "overview";

const readInsightFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  return normalizeInsight(params.get("insight") || "overview");
};

const readTimeframeParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    timeframe: params.get("timeframe") || "last_30_days",
    startDate: params.get("start_date") || null,
    endDate: params.get("end_date") || null,
  };
};

const timeframeKey = ({ timeframe, startDate, endDate }) =>
  `${timeframe}|${startDate || ""}|${endDate || ""}`;

const WebsiteInsightsTabs = (props) => {
  const { website, insightsUrl, topCampaignsUrl, customFieldsUrl } = props;

  const [activeInsight, setActiveInsight] = useState(
    normalizeInsight(props.initialInsight)
  );
  const [visited, setVisited] = useState(() => ({
    [normalizeInsight(props.initialInsight)]: true,
  }));
  const [timeframeParams, setTimeframeParams] = useState(readTimeframeParams);
  const [topCampaignsCache, setTopCampaignsCache] = useState({});
  const [fieldsState, setFieldsState] = useState({
    status: "idle",
    fields: [],
  });

  const tfKey = timeframeKey(timeframeParams);

  // Keep the active tab and timeframe in sync with the URL (back/forward,
  // timeframe changes dispatched by child components).
  useEffect(() => {
    const handlePopState = () => {
      const nextInsight = readInsightFromUrl();
      setActiveInsight(nextInsight);
      setVisited((prev) => ({ ...prev, [nextInsight]: true }));
      setTimeframeParams(readTimeframeParams());
    };
    const handleTimeframeChange = () => {
      setTimeframeParams(readTimeframeParams());
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener(
      INSIGHTS_TIMEFRAME_CHANGE_EVENT,
      handleTimeframeChange
    );
    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener(
        INSIGHTS_TIMEFRAME_CHANGE_EVENT,
        handleTimeframeChange
      );
    };
  }, []);

  const needsTopCampaigns = visited.overview || visited.campaigns;
  const needsFields = visited.overview || visited.custom_fields;

  // Fetch top campaigns lazily per timeframe key.
  useEffect(() => {
    if (!needsTopCampaigns) return;
    if (topCampaignsCache[tfKey]) return;

    setTopCampaignsCache((prev) => ({
      ...prev,
      [tfKey]: { status: "loading" },
    }));

    const url = new URL(topCampaignsUrl, window.location.origin);
    url.searchParams.set("view", "campaigns");
    url.searchParams.set("timeframe", timeframeParams.timeframe);
    if (timeframeParams.startDate) {
      url.searchParams.set("start_date", timeframeParams.startDate);
    }
    if (timeframeParams.endDate) {
      url.searchParams.set("end_date", timeframeParams.endDate);
    }

    let cancelled = false;
    fetch(url.toString(), { headers: { Accept: "application/json" } })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        setTopCampaignsCache((prev) => ({
          ...prev,
          [tfKey]: { status: "loaded", data: data.top_campaigns },
        }));
      })
      .catch(() => {
        if (cancelled) return;
        setTopCampaignsCache((prev) => ({
          ...prev,
          [tfKey]: { status: "error" },
        }));
      });

    return () => {
      cancelled = true;
    };
  }, [needsTopCampaigns, tfKey, topCampaignsUrl]);

  // Fetch custom fields lazily once (timeframe independent).
  useEffect(() => {
    if (!needsFields) return;
    if (fieldsState.status !== "idle") return;

    setFieldsState({ status: "loading", fields: [] });

    fetch(customFieldsUrl, { headers: { Accept: "application/json" } })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        setFieldsState({ status: "loaded", fields: data.fields || [] });
      })
      .catch(() => {
        setFieldsState({ status: "error", fields: [] });
      });
  }, [needsFields, fieldsState.status, customFieldsUrl]);

  const refetchTopCampaigns = useCallback(() => {
    setTopCampaignsCache((prev) => {
      const next = { ...prev };
      delete next[tfKey];
      return next;
    });
  }, [tfKey]);

  const handleSelectInsight = useCallback(
    (key) => {
      const nextInsight = normalizeInsight(key);
      const url = new URL(insightsUrl, window.location.origin);
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.forEach((value, paramKey) => {
        if (!["insight", "page", "format"].includes(paramKey)) {
          url.searchParams.set(paramKey, value);
        }
      });
      if (nextInsight !== "overview") {
        url.searchParams.set("insight", nextInsight);
      }

      window.history.pushState({ insight: nextInsight }, "", url.toString());
      setActiveInsight(nextInsight);
      setVisited((prev) => ({ ...prev, [nextInsight]: true }));
      // Keep export hrefs / listeners current for the new tab.
      window.dispatchEvent(new CustomEvent(INSIGHTS_TIMEFRAME_CHANGE_EVENT));
    },
    [insightsUrl]
  );

  const handleTopCampaignsTimeframeChange = useCallback(
    ({ timeframe, startDate, endDate }) => {
      syncInsightsTimeframeParamsToUrl(insightsUrl, {
        timeframe,
        startDate,
        endDate,
      });
    },
    [insightsUrl]
  );

  const topCampaignsEntry = topCampaignsCache[tfKey] || { status: "loading" };
  const topCampaignsData = topCampaignsEntry.data;
  const topCampaignsLoading = topCampaignsEntry.status === "loading";
  const topCampaignsError = topCampaignsEntry.status === "error";

  const overviewTopCampaigns = useMemo(
    () =>
      topCampaignsData
        ? {
            ...topCampaignsData,
            campaigns: (topCampaignsData.campaigns || []).slice(
              0,
              OVERVIEW_TOP_CAMPAIGNS_LIMIT
            ),
          }
        : topCampaignsData,
    [topCampaignsData]
  );

  // Export URLs read the current query string, which changes with the timeframe
  // (tfKey), so recompute when either the base url or timeframe changes.
  const csvExportTriggerUrl = useMemo(
    () => buildInsightsCsvExportUrl(insightsUrl),
    [insightsUrl, tfKey]
  );

  const pdfExportTriggerUrl = useMemo(
    () => buildInsightsPdfExportUrl(insightsUrl),
    [insightsUrl]
  );

  const websiteWithFields = useMemo(
    () => ({ ...website, fields: fieldsState.fields }),
    [website, fieldsState.fields]
  );

  const renderPanel = (key, content) => {
    if (!visited[key]) return null;
    const isActive = activeInsight === key;
    return (
      <div
        key={key}
        className="website-insights-panel row hard-center"
        style={isActive ? {} : { display: "none" }}
      >
        {content}
      </div>
    );
  };

  return (
    <div className="WebsiteInsightsTabs">
      <div className="row hard-center insights-toolbar-row">
        <div className="col-xs-12 no-padding-sides some-vertical-space">
          <WebsiteInsightsToolbar
            insightsUrl={insightsUrl}
            selectedInsight={
              activeInsight === "overview" ? null : activeInsight
            }
            insightUrls={props.insightUrls}
            onSelectInsight={handleSelectInsight}
          />
        </div>
      </div>

      {renderPanel(
        "overview",
        <React.Fragment>
          <div className="col-md-12">
            <div className="col-xs-12 border-solid border-[1px] border-slate-200 rounded-xl shadow-sm some-vertical-space">
              <div className="col-xs-12">
                <WebsiteConversionReport
                  website={website}
                  timeframeTypes={props.timeframeTypes}
                  this_month={props.thisMonth}
                  today={props.today}
                  shopify_revenue={props.shopifyRevenue}
                  shopify_orders={props.shopifyOrders}
                  contact_subscribe_reports={props.contactSubscribeReports}
                  currency={props.currency}
                  syncInsightsTimeframe={true}
                  insightsUrl={insightsUrl}
                  exportTriggerUrl={csvExportTriggerUrl}
                  exportFormat="csv"
                  initialTimeframeLabel={props.conversionTimeframeLabel}
                  initialStartDate={props.startDate}
                  initialEndDate={props.endDate}
                />
              </div>
            </div>
          </div>

          <div className="col-md-12 mt-10 insight">
            <div className="col-xs-12 border-solid border-[1px] border-slate-200 rounded-xl shadow-sm some-vertical-space">
              <div className="col-xs-12">
                <WebsiteAiInsights
                  websiteId={website.id}
                  website={website}
                  remixAiGated={props.remixAiGated}
                  aiDisabled={props.aiDisabled}
                  websiteSettingsUrl={props.websiteSettingsUrl}
                  exportableInsight={props.exportableInsight}
                  exportTriggerUrl={pdfExportTriggerUrl}
                  timeframeOptions={props.timeframeOptions}
                  aiInsightsLoading={props.aiInsightsLoading}
                  aiInsightsStatusUrl={props.aiInsightsStatusUrl}
                  websiteRecommendations={props.websiteRecommendations}
                  playbooksById={props.playbooksById}
                  campaignSlides={props.campaignSlides}
                  analysisPeriod={props.analysisPeriod}
                  currency={props.currency}
                  aiInsightsSettings={props.aiInsightsSettings}
                  aiInsightsCampaignOptions={props.aiInsightsCampaignOptions}
                />
              </div>
            </div>
          </div>

          <div id="top-campaigns-overview" className="col-md-6 mt-10 insight">
            <div className="row hard-center">
              <div className="col-xs-12 border-solid border-[1px] border-slate-200 rounded-xl shadow-sm some-vertical-space">
                <WebsiteTopCampaigns
                  website={website}
                  topCampaigns={overviewTopCampaigns}
                  timeframeOptions={props.timeframeOptions}
                  selectedTimeframe={timeframeParams.timeframe}
                  startDate={timeframeParams.startDate}
                  endDate={timeframeParams.endDate}
                  insightsUrl={insightsUrl}
                  overview={true}
                  currency={props.currency}
                  onTimeframeChange={handleTopCampaignsTimeframeChange}
                  loading={topCampaignsLoading}
                  loadError={topCampaignsError}
                  onRetry={refetchTopCampaigns}
                />
              </div>
            </div>
          </div>

          <div className="col-md-6 mt-10 insight">
            <div className="row hard-center">
              <div
                className="col-xs-12 border-solid border-[1px] border-slate-200 rounded-xl shadow-sm some-vertical-space"
                style={{ height: "452px" }}
              >
                <div className="col-xs-12">
                  <WebsiteContactSubscribePages
                    website={website}
                    timeframeTypes={props.timeframeTypes}
                    overview={true}
                    feature_gate={props.overviewFeatureGate}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6 mt-10 insight">
            <div className="row hard-center">
              <div
                className="col-xs-12 border-solid border-[1px] border-slate-200 rounded-xl shadow-sm some-vertical-space"
                style={{ height: "452px" }}
              >
                <div className="col-xs-12">
                  <WebsiteContactLandingPages
                    website={website}
                    timeframeTypes={props.timeframeTypes}
                    overview={true}
                    feature_gate={props.overviewFeatureGate}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6 mt-10 insight">
            <div className="row hard-center">
              <div
                className="col-xs-12 border-solid border-[1px] border-slate-200 rounded-xl shadow-sm some-vertical-space"
                style={{ height: "452px" }}
              >
                <div className="col-xs-12">
                  <WebsiteContactReferralSources
                    website={website}
                    timeframeTypes={props.timeframeTypes}
                    overview={true}
                    feature_gate={props.overviewFeatureGate}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6 mt-10 insight">
            <div className="row hard-center">
              <div
                className="col-xs-12 border-solid border-[1px] border-slate-200 rounded-xl shadow-sm some-vertical-space"
                style={{ height: "452px" }}
              >
                <div className="col-xs-12">
                  <WebsiteContactUTMs
                    website={website}
                    timeframeTypes={props.timeframeTypes}
                    overview={true}
                    feature_gate={props.overviewFeatureGate}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6 mt-10 insight">
            <div className="row hard-center">
              <div
                className="col-xs-12 border-solid border-[1px] border-slate-200 rounded-xl shadow-sm some-vertical-space"
                style={{ height: "452px" }}
              >
                <div className="col-xs-12">
                  <WebsiteCustomFields
                    website={websiteWithFields}
                    timeframeTypes={props.timeframeTypes}
                    overview={true}
                    currency={props.currency}
                    gateReports={props.gateReports}
                    idPrefix="overview-"
                  />
                </div>
              </div>
            </div>
          </div>
        </React.Fragment>
      )}

      {renderPanel(
        "campaigns",
        <div id="top-campaigns" className="col-md-12 some-vertical-space">
          <div className="row hard-center">
            <div
              className="col-xs-12 border-solid border-[1px] border-slate-200 rounded-xl shadow-sm smooth"
              style={{ paddingBottom: "30px" }}
            >
              <WebsiteTopCampaigns
                website={website}
                topCampaigns={topCampaignsData}
                timeframeOptions={props.timeframeOptions}
                selectedTimeframe={timeframeParams.timeframe}
                startDate={timeframeParams.startDate}
                endDate={timeframeParams.endDate}
                insightsUrl={insightsUrl}
                overview={false}
                currency={props.currency}
                onTimeframeChange={handleTopCampaignsTimeframeChange}
                loading={topCampaignsLoading}
                loadError={topCampaignsError}
                onRetry={refetchTopCampaigns}
              />
            </div>
          </div>
        </div>
      )}

      {CONTACT_TABS.map(({ key, Component }) =>
        renderPanel(
          key,
          <div className="col-md-12 some-vertical-space">
            <div className="row hard-center">
              <div
                className="col-xs-12 border-solid border-[1px] border-slate-200 rounded-xl shadow-sm smooth"
                style={{ paddingBottom: "30px" }}
              >
                <Component
                  website={website}
                  timeframeTypes={props.timeframeTypes}
                  feature_gate={props.fullFeatureGate}
                />
              </div>
            </div>
          </div>
        )
      )}

      {renderPanel(
        "custom_fields",
        <div className="col-md-12 some-vertical-space insight">
          <div className="row hard-center">
            <div className="col-xs-12 border-solid border-[1px] border-slate-200 rounded-xl shadow-sm smooth">
              <WebsiteCustomFields
                website={websiteWithFields}
                timeframeTypes={props.timeframeTypes}
                currency={props.currency}
                gateReports={props.gateReports}
              />
            </div>
          </div>
        </div>
      )}

      {renderPanel(
        "ai_insights",
        <div className="col-md-12 some-vertical-space">
          <div className="row hard-center">
            <div className="col-xs-12 border-solid border-[1px] border-slate-200 rounded-xl shadow-sm">
              <div className="col-xs-12">
                <WebsiteAiInsights
                  websiteId={website.id}
                  website={website}
                  remixAiGated={props.remixAiGated}
                  aiDisabled={props.aiDisabled}
                  websiteSettingsUrl={props.websiteSettingsUrl}
                  exportableInsight={props.exportableInsight}
                  exportTriggerUrl={pdfExportTriggerUrl}
                  timeframeOptions={props.timeframeOptions}
                  aiInsightsLoading={props.aiInsightsLoading}
                  aiInsightsStatusUrl={props.aiInsightsStatusUrl}
                  websiteRecommendations={props.websiteRecommendations}
                  playbooksById={props.playbooksById}
                  campaignSlides={props.campaignSlides}
                  analysisPeriod={props.analysisPeriod}
                  currency={props.currency}
                  aiInsightsSettings={props.aiInsightsSettings}
                  aiInsightsCampaignOptions={props.aiInsightsCampaignOptions}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

WebsiteInsightsTabs.propTypes = {
  website: PropTypes.object.isRequired,
  initialInsight: PropTypes.string,
  insightsUrl: PropTypes.string.isRequired,
  insightUrls: PropTypes.object.isRequired,
  topCampaignsUrl: PropTypes.string.isRequired,
  customFieldsUrl: PropTypes.string.isRequired,
  timeframeTypes: PropTypes.array.isRequired,
  timeframeOptions: PropTypes.array.isRequired,
  conversionTimeframeLabel: PropTypes.string,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  thisMonth: PropTypes.string,
  today: PropTypes.string,
  shopifyRevenue: PropTypes.any,
  shopifyOrders: PropTypes.any,
  contactSubscribeReports: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      last_30_days: PropTypes.number,
      fetched_at: PropTypes.string,
      capped: PropTypes.bool,
    })
  ),
  currency: PropTypes.string,
  overviewFeatureGate: PropTypes.bool,
  fullFeatureGate: PropTypes.bool,
  gateReports: PropTypes.bool,
  remixAiGated: PropTypes.bool,
  exportableInsight: PropTypes.bool,
  aiDisabled: PropTypes.bool,
  websiteSettingsUrl: PropTypes.string,
  aiInsightsLoading: PropTypes.bool,
  aiInsightsStatusUrl: PropTypes.string,
  websiteRecommendations: PropTypes.object,
  playbooksById: PropTypes.object,
  campaignSlides: PropTypes.arrayOf(PropTypes.object),
  analysisPeriod: PropTypes.object,
  aiInsightsSettings: PropTypes.object,
  aiInsightsCampaignOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ),
};

export default WebsiteInsightsTabs;
